import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
export default function CSS_ImageStyling() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🖼️ Advanced CSS: Image Styling</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS provides powerful ways to style images, from shaping and positioning
        to adding effects like shadows, filters, and overlays. You can make
        images visually appealing without editing them in external software.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>border-radius</code> for rounded or circular images.
          </li>
          <li>
            Apply <code>border</code> for stylish frames.
          </li>
          <li>
            <code>object-fit</code> controls how images fit inside a container.
          </li>
          <li>
            <code>object-position</code> aligns the image within its container.
          </li>
          <li>
            CSS <code>filter</code> can add blur, grayscale, brightness, or
            contrast.
          </li>
          <li>
            Add <code>box-shadow</code> for depth and emphasis.
          </li>
        </ul>
      </div>

      {/* Example 1: Rounded Corners */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Rounded Corners</h2>
        <CodeCopyBlock
          code={`img {
  border-radius: 20px;
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Rounded example"
          style={{ borderRadius: "20px", marginTop: "1rem", width: "250px" }}
        />
      </div>

      {/* Example 2: Image Borders */}
      <div className="tutorial-section">
        <h2 className="section-title">🖌️ Image Borders</h2>
        <CodeCopyBlock
          code={`img {
  border: 5px solid #3b82f6;
  border-radius: 15px;
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=300&q=80"
          alt="Border example"
          style={{
            border: "5px solid #3b82f6",
            borderRadius: "15px",
            marginTop: "1rem",
            width: "250px",
          }}
        />
      </div>

      {/* Example 3: Object-fit & Object-position */}
      <div className="tutorial-section">
        <h2 className="section-title">📐 Object-fit & Object-position</h2>
        <CodeCopyBlock
          code={`div {
  width: 250px;
  height: 150px;
  overflow: hidden;
}

img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* contain, fill, cover, scale-down */
  object-position: center top;
}`}
        />
        <div
          style={{
            width: "250px",
            height: "150px",
            overflow: "hidden",
            marginTop: "1rem",
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=400&q=80"
            alt="Object-fit example"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
            }}
          />
        </div>
      </div>

      {/* Example 4: Image Filters */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Image Filters</h2>
        <CodeCopyBlock
          code={`img {
  filter: grayscale(80%) blur(2px) brightness(120%);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=300&q=80"
          alt="Filter example"
          style={{
            filter: "grayscale(80%) blur(2px) brightness(120%)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Example 5: Box Shadow */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Box Shadow</h2>
        <CodeCopyBlock
          code={`img {
  box-shadow: 5px 5px 15px rgba(0,0,0,0.4);
}`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=300&q=80"
          alt="Box shadow example"
          style={{
            boxShadow: "5px 5px 15px rgba(0,0,0,0.4)",
            marginTop: "1rem",
            width: "250px",
            borderRadius: "12px",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine filters, shadows, and border-radius for stylish images.
          </li>
          <li>
            Use <code>object-fit</code> to handle responsive image containers.
          </li>
          <li>
            Experiment with gradient overlays on images using{" "}
            <code>background-image</code>.
          </li>
          <li>
            Apply CSS animations on images for hover or attention effects.
          </li>
          <li>Optimize images for web performance while styling with CSS.</li>
        </ul>
      </div>
    </div>
  );
}
